import { MenuItemWithIcon } from "@msys/ui";
import { Divider, ListSubheader, Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import moment from "moment";
import { useOpenFile } from "../../commons/hooks/useOpenFile.js";
import { PdfFileIcon } from "../../commons/icons/PdfFileIcon.js";
import {
  QuoteDocuments_QuoteDocumentFragment,
  QuoteDocuments_QuoteFragment,
} from "./QuoteDocuments.generated.js";

/**
 * The saved-document list lives inside the header's PDF menu rather than in a
 * box on the quote body, so every quote view reaches it through the same
 * affordance that generates them.
 */
export function QuoteDocumentsMenuItems({
  quote,
  showDivider,
}: {
  quote: QuoteDocuments_QuoteFragment;
  showDivider: boolean;
}) {
  const { t } = useTranslate(["Quote"]);

  return (
    <>
      {showDivider && <Divider key="divider-documents" />}
      <ListSubheader
        key="header-documents"
        sx={{ color: "grey.500", minHeight: "36px", lineHeight: "36px" }}
      >
        {t("Saved documents", { ns: "Quote" })}
      </ListSubheader>
      {quote.documents.length === 0 ? (
        <MenuItemWithIcon key="documents-empty" inset disabled>
          {t("No documents saved yet", { ns: "Quote" })}
        </MenuItemWithIcon>
      ) : (
        quote.documents.map(document => (
          <QuoteDocumentMenuItem key={document.id} document={document} />
        ))
      )}
    </>
  );
}

function QuoteDocumentMenuItem({
  document,
}: {
  document: QuoteDocuments_QuoteDocumentFragment;
}) {
  const { t } = useTranslate(["Quote"]);
  const { openPdf } = useOpenFile();

  const author = [document.createdBy.firstname, document.createdBy.familyname]
    .filter(Boolean)
    .join(" ");

  return (
    <MenuItemWithIcon
      icon={<PdfFileIcon fontSize="small" />}
      onClick={async e => {
        e.preventDefault();
        e.stopPropagation();
        await openPdf(document.file.url, document.name);
      }}
      sx={{ whiteSpace: "normal" }}
    >
      <Stack direction="column">
        <Typography variant="body2">{document.name}</Typography>
        <Typography variant="caption" color="text.secondary">
          {[
            moment(document.createdAt).format("L LT"),
            author,
            document.source === "upload"
              ? t("Uploaded", { ns: "Quote" })
              : t("Generated", { ns: "Quote" }),
          ]
            .filter(Boolean)
            .join(" · ")}
        </Typography>
      </Stack>
    </MenuItemWithIcon>
  );
}
